import type { Ref } from "react";
import { ChevronRight } from "lucide-react";
import { Badge, Button, Card } from "@repo/ui";
import {
  ATTENTES,
  LIBELLE_ATTENTE,
  libelleZone,
  type Echelle,
} from "@convex/referentiel/fourchette";
import {
  estComplete,
  grouper,
  prochaineIncomplete,
  SECTIONS,
  zoneDe,
  type Dimension,
  type Etat,
  type ValeurDimension,
} from "./fourchettes-modele";

type Props = {
  dimensions: Dimension[];
  etat: Etat;
  echelle: Echelle;
  lectureSeule: boolean;
  enCoursEnvoi: boolean;
  /** Titre du récapitulatif : reçoit le focus quand il arrive. */
  titreRef: Ref<HTMLHeadingElement>;
  onAller: (position: number) => void;
  onValider: () => void;
};

/** Zone en clair : « 7 à 10 », « Hors calcul ». */
function libelleReponse(valeur: ValeurDimension, echelle: Echelle): string {
  if (valeur.attente === "aucune") return "Hors calcul";
  const zone = zoneDe(valeur, echelle);
  return zone ? libelleZone(zone) : "—";
}

/**
 * Relecture des 20 dimensions avant validation. Le carrousel n'en montre
 * qu'une à la fois : c'est ici que le référent retrouve la vue d'ensemble et
 * repère un déséquilibre (tout en « score élevé », par exemple) — d'où la
 * répartition par attente en tête. Chaque ligne ramène à sa dimension.
 *
 * C'est aussi l'écran d'une contribution validée (lecture seule) : il dit tout
 * d'un coup d'œil, sans rien à valider.
 */
export function RecapitulatifFourchettes({
  dimensions,
  etat,
  echelle,
  lectureSeule,
  enCoursEnvoi,
  titreRef,
  onAller,
  onValider,
}: Props) {
  const manquantes = dimensions.filter((d) => !estComplete(etat[d.code])).length;
  // Ordre de l'écran de saisie : du score bas au score élevé, puis sans attente.
  const repartition = ATTENTES.map((attente) => ({
    attente,
    nombre: dimensions.filter((d) => etat[d.code]?.attente === attente).length,
  }));

  return (
    <Card className="gap-0 py-0">
      <header className="px-5 pt-4 pb-3">
        <h4
          ref={titreRef}
          tabIndex={-1}
          className="text-lg font-semibold text-charte-strong outline-none"
        >
          Récapitulatif
        </h4>
        <p className="text-sm tabular-nums text-charte-dim">
          {repartition
            .map(({ attente, nombre }) => `${LIBELLE_ATTENTE[attente]} : ${nombre}`)
            .join(" · ")}
        </p>
      </header>

      {grouper(dimensions).map(([categorie, membres]) => (
        <section key={categorie || "sans-categorie"} className="border-t border-charte-line">
          {SECTIONS[categorie] && (
            <h5 className="px-5 pt-3 pb-1 text-sm font-semibold text-charte-muted">
              {SECTIONS[categorie].titre}
            </h5>
          )}
          <ul className="pb-2">
            {membres.map(({ dimension, index }) => {
              const valeur = etat[dimension.code] ?? {};
              return (
                <li key={dimension.code}>
                  <button
                    type="button"
                    onClick={() => onAller(index)}
                    className="group grid w-full cursor-pointer grid-cols-[1fr_auto] items-center gap-x-4 gap-y-0.5 px-5 py-2 text-left text-sm transition-colors duration-150 hover:bg-charte-hover focus-visible:bg-charte-hover focus-visible:outline-none sm:grid-cols-[minmax(0,14rem)_13rem_1fr_auto]"
                  >
                    <span className="font-medium text-charte-strong">{dimension.label}</span>
                    {estComplete(valeur) ? (
                      <>
                        <span className="col-start-1 text-charte-soft sm:col-start-auto">
                          {valeur.attente && LIBELLE_ATTENTE[valeur.attente]}
                        </span>
                        <span className="col-start-1 tabular-nums text-charte-dim sm:col-start-auto">
                          {libelleReponse(valeur, echelle)}
                        </span>
                      </>
                    ) : (
                      <span className="col-start-1 sm:col-span-2 sm:col-start-auto">
                        <Badge variant="outline">À renseigner</Badge>
                      </span>
                    )}
                    <ChevronRight
                      aria-hidden
                      className="col-start-2 row-span-3 row-start-1 h-4 w-4 text-charte-ghost transition-colors group-hover:text-charte-strong sm:col-start-4 sm:row-span-1"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {!lectureSeule && (
        <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-charte-line px-5 py-4">
          {manquantes > 0 && (
            <>
              <p className="mr-auto text-sm text-charte-muted">
                {manquantes} dimension{manquantes > 1 ? "s" : ""} encore incomplète
                {manquantes > 1 ? "s" : ""}
              </p>
              <Button
                variant="outline"
                onClick={() => onAller(prochaineIncomplete(dimensions, etat, -1))}
              >
                Compléter
              </Button>
            </>
          )}
          <Button
            disabled={enCoursEnvoi || manquantes > 0}
            onClick={onValider}
          >
            Valider ma contribution
          </Button>
        </footer>
      )}
    </Card>
  );
}
